import { logger } from "src/core/globals";

import { HEART_BEAT, MAX_COOLDOWN } from "./constants";
import { Observable } from "./observable";
import { EVENT, Payload } from "./types";

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
abstract class Socket<T> extends Observable<any> {
  static RECONNECT: EVENT = "reconnect";
  static CONNECT: EVENT = "connect";
  static OPEN: EVENT = "open";
  static MESSAGE: EVENT = "message";
  static ERROR: EVENT = "error";
  static CLOSE: EVENT = "close";

  private keepAliveTimerId: NodeJS.Timeout | null = null;
  protected pingTimeoutId: NodeJS.Timeout | null = null;

  public reconnect: boolean = true;
  public connected: boolean = false;
  /* Protected instance variables */
  protected socket: WebSocket | undefined;
  protected binaryType: BinaryType = "blob";
  /**
   * queue will buffer messages if the
   * websocket is not connected. These
   * messages will be sent when the
   * websocket is connected.
   */
  protected queue: T[] = [];

  protected cooldown = 500;

  constructor() {
    super();
    this.addEvents([Socket.ERROR, Socket.OPEN, Socket.MESSAGE, Socket.CLOSE]);
  }

  /**
   * The init method initializes the WebSocket connection.
   * It checks the current state of the WebSocket and, if
   * it is not already connecting or open, creates a new
   * WebSocket instance.
   *
   * Accepts a URL string or a builder function.  A builder
   * is re-invoked on every reconnect so the resolved host
   * can change at runtime (e.g. after a REST redirect
   * updates `window.cnc.activeApiOrigin`).
   */
  init(url: string | (() => string)) {
    const resolved = typeof url === "string" ? url : url();

    try {
      const readyState = this.socket?.readyState;
      switch (readyState) {
        /**
         * There exists a race condition
         * where if we try to send a flurry
         * of requests the socket will
         * open again and again while it
         * is connecting. If we are already
         * connecting just return and pass the
         * request. it will be queued and sent
         * when the socket is open.
         *
         * Also, a small chance it
         * opened while this was sent.
         */
        case WebSocket.CONNECTING:
        case WebSocket.OPEN:
          return;
        default: {
          const wsUrl = new URL(resolved);
          if (window.cnc.xCsrfToken) {
            wsUrl.searchParams.append("xCsrfToken", window.cnc.xCsrfToken);
          }
          const ws = new WebSocket(wsUrl);
          ws.binaryType = this.binaryType;
          /**
           * Event handlers are set up for the WebSocket
           * to handle onopen, onmessage, onerror, and
           * onclose events.
           * For example, the onopen event
           * handler sets the connected flag to true, sends
           * any queued messages, and starts the keep-alive
           * mechanism
           *
           * the instance methods are wrapped in the arrow
           * function to ensure `this` refers to the instance
           * and not the underlying WebSocket.
           */
          ws.onopen = () => {
            this.onOpen();
          };
          ws.onmessage = (m: MessageEvent<string | ArrayBuffer>) => {
            this.onMessage(m);
          };
          ws.onerror = (e: Event) => {
            logger.error(e);
          };
          ws.onclose = (e: CloseEvent) => {
            this.onClose(url, e);
          };

          this.socket = ws;
        }
      }
    } catch (e: unknown) {
      logger.error(e);
    }
  }

  close() {
    /* clean up the keep alive interval */
    if (this.keepAliveTimerId) {
      clearInterval(this.keepAliveTimerId);
    }
    /* clean up the ping timeout */
    if (this.pingTimeoutId) {
      clearTimeout(this.pingTimeoutId);
    }
    this.reconnect = false;
    this.socket?.close();
  }

  /**
   * Keeps the WebSocket connection alive by sending periodic ping messages.
   *
   * This method sets up an interval that sends a ping message
   * to the WebSocket server every 30 seconds. The interval is
   * stored in `this.keepAliveTimerId` so it can be cleared
   * later if needed.
   */
  keepAlive() {
    if (this.keepAliveTimerId) {
      clearInterval(this.keepAliveTimerId);
    }
    this.keepAliveTimerId = setInterval(() => {
      if (this.connected) {
        const msg = this.getKeepAliveMessage();
        this.send(msg);
        /**
         * when we send a ping message, we
         * also set a timeout to close the
         * socket if we do not get a response
         * in 5 seconds.
         */
        if (this.pingTimeoutId) {
          clearTimeout(this.pingTimeoutId);
        }
        this.pingTimeoutId = setTimeout(() => {
          this.socket?.close();
        }, 5000);
      }
    }, HEART_BEAT);
  }

  broadcast(msg: T) {
    this.fireEvent(Socket.MESSAGE, msg);
  }

  send(msg: T) {
    if (this.connected && this.socket?.readyState === WebSocket.OPEN) {
      try {
        this.socket?.send(this.encode(msg));
        return;
      } catch (e: unknown) {
        // we do nothing with this.
        logger.error(e);
      }
    }
    /**
     * Since there is a chance that a
     * component might send a message
     * before the socket is open, this
     * will add queue up those messages,
     * they will be sent when the
     * socket opens.
     */
    this.queue.push(msg);
  }

  onOpen(): void {
    this.connected = true;
    this.fireEvent(Socket.OPEN, true);
    this.queue.forEach((msg: T) => {
      if (msg) {
        this.send(msg);
      }
    });
    /* once the messages have been sent reset the queue */
    this.queue = [];
    this.cooldown = 500;
    this.keepAlive();
  }

  onMessage(m: MessageEvent<string | ArrayBuffer>) {
    /**
     * If we get any message on the socket
     * after we sent the ping we can clear
     * the ping timeout. This is just to
     * be sure if we send a ping and get
     * no response we close the socket
     * and reconnect.
     */
    if (this.pingTimeoutId) {
      clearTimeout(this.pingTimeoutId);
    }
    this.broadcast(this.decode(m));
  }

  onClose(url: string | (() => string), e: CloseEvent) {
    if (this.reconnect) {
      setTimeout(() => {
        this.init(url);
        this.fireEvent(Socket.RECONNECT, true);
        /**
         * Each time you try to reconnect double
         * the cooldown interval up to 10 seconds,
         * so we do not spam the server if you
         * cannot connect.
         */
        this.cooldown = Math.min(this.cooldown * 2, MAX_COOLDOWN);
      }, this.cooldown);
    }
    this.connected = false;
    this.queue.length = 0;
    this.fireEvent(Socket.CLOSE, e);
  }

  abstract decode(m: MessageEvent<Payload>): T;
  abstract encode(msg: T): Payload;
  abstract getKeepAliveMessage(): T;
}

export { Socket, type Payload };
